import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearWorkflowModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import AddWorkflow from "./components/AddWorkflow";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import TableComponent from "../../components/common/TableComponent";
import EditWorkflow from "./components/EditWorkflow";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function WorkFlow() {
  const {
    statusChangeModel,
    setStatusChangeModel,
    workflowModel,
    setWorkflowModel,
    workflowData,
    WORKFLOW_ENDPOINT,
    config,
    pageIndexWorkflow,
    setPageIndexWorkflow,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [workflowDataFiltered, setWorkflowDatafiltered] = useState(
    workflowData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const workflowDataObj = workflowData.Data.find(
        (workflowObj) => workflowObj.Id === Number(id)
      );
      setWorkflowModel(workflowDataObj);
    }
  }, [id]);

  useEffect(() => {
    setWorkflowDatafiltered(workflowData.Data);
  }, [workflowData]);

  const validateOperationResult = (result, isDelete) => {
    setWorkflowModel(getClearWorkflowModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([WORKFLOW_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexWorkflow !== 0 && workflowModel.Data.length === 1) {
        setPageIndexWorkflow(pageIndexWorkflow - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${WORKFLOW_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${WORKFLOW_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${WORKFLOW_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !workflowModel?.Name ||
    workflowModel?.Name.replaceAll(" ", "").length <= 0 ||
    workflowModel?.InitialStatusId <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.workflows}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={WORKFLOW_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="workflow"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_workflow}
          onClickToSubmit={async () => {
            await handleUpdateRequest(workflowModel);
            setShowEditModal(false);
            setWorkflowModel(getClearWorkflowModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setWorkflowModel(getClearWorkflowModel());
          }}
          Component={<EditWorkflow />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.workflows}
            subtitle={localize.workflow_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="workflow_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_workflow}
                onClickToSubmit={async () => {
                  await handleCreateRequest(workflowModel);
                  setShowAddModal(false);
                  setWorkflowModel(getClearWorkflowModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setWorkflowModel(getClearWorkflowModel());
                }}
                Component={
                  <AddWorkflow
                    model={workflowModel}
                    setModel={setWorkflowModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={workflowModel?.Name}
          entityType={localize.workflow}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setWorkflowModel(getClearWorkflowModel());
            setShowDeleteModal(false);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(workflowModel?.Id);
            setShowDeleteModal(false);
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexWorkflow}
            setPageIndex={setPageIndexWorkflow}
            dataArray={workflowData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          workflowData.Data?.length !== 0
            ? workflowDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setWorkflowModel(item);
                        setStatusChangeModel({
                          WorkFlowId: item.Id,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setWorkflowModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
